import { Component, Inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MeetingSchedulerNavActionButton } from '../interface';
import { BreadCrumb } from '../sales-center-navigation/breadcrumbs.component';
import { MeetingSchedulerNavigationBreadcrumbService } from './navigation-breadcrumbs.service';
import { CALENDAR_VIEW_PAGE_LINK_TOKEN, MEETING_TYPE_LIST_VIEW_PAGE_TOKEN } from '../data-providers/providers';

@Component({
  selector: 'meeting-scheduler-navigation',
  templateUrl: './meeting-scheduler-navigation.component.html',
  styleUrls: ['./meeting-scheduler-navigation.component.scss'],
  standalone: false,
})
export class MeetingSchedulerNavigationComponent implements OnInit, OnDestroy {
  private readonly breadcrumbs$: Observable<BreadCrumb[]>;
  protected readonly previousPage$: Observable<{ text: string; url: string } | null>;
  protected readonly currentPageTitle$: Observable<string>;
  protected readonly actions: Signal<MeetingSchedulerNavActionButton[]>;
  private readonly subscriptions: Subscription[] = [];

  hideBackNavigation = false;
  displayMyMeetingsActions = false;
  isSessionScheduleFlow = false;

  constructor(
    private readonly navigationService: MeetingSchedulerNavigationBreadcrumbService,
    private readonly router: Router,
    @Inject(CALENDAR_VIEW_PAGE_LINK_TOKEN) private readonly calendarViewPage$: Observable<string>,
    @Inject(MEETING_TYPE_LIST_VIEW_PAGE_TOKEN) private readonly meetingTypeListViewPage$: Observable<string>,
    private readonly translate: TranslateService,
  ) {
    this.breadcrumbs$ = this.navigationService.breadcrumbs$.pipe(
      map((msBreadcrumbs) => {
        return (msBreadcrumbs || []).map((b) => {
          return {
            label: b.text,
            link: b.url || undefined, // an empty string link will break the nav
          };
        });
      }),
    );

    this.previousPage$ = combineLatest([this.breadcrumbs$, this.calendarViewPage$, this.meetingTypeListViewPage$]).pipe(
      map(([breadcrumbs, calendarViewPage, meetingTypeListViewPage]) => {
        // Check current route for specific patterns
        const urlSegments = this.router.url.split('/');
        const currentUrl = this.router.url;

        // Check if this is a session scheduling flow
        const urlParams = new URLSearchParams(currentUrl.split('?')[1] || '');
        const calendarType = urlParams.get('calendar_type');
        const showScheduleAccordion = urlParams.get('show_schedule_accordion');
        const isSessionScheduleFlow = calendarType === 'session' && showScheduleAccordion === 'true';
        const isEventType = urlSegments.includes('event-type');

        // Update the component property for template usage
        this.isSessionScheduleFlow = isSessionScheduleFlow && isEventType;

        // For session scheduling flow, always navigate to meeting-type page
        if (this.isSessionScheduleFlow) {
          return {
            text: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.BREADCRUMB_V2'),
            url: meetingTypeListViewPage,
          };
        }

        const shouldNavigateToMeetingType =
          urlSegments.includes('event-type') || urlSegments.includes('group') || urlSegments.includes('service');

        // Route-based navigation logic
        if (shouldNavigateToMeetingType && !this.isSessionScheduleFlow) {
          return {
            text: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.BREADCRUMB_V2'),
            url: meetingTypeListViewPage,
          };
        }

        // Try to get the previous page from breadcrumbs (second-to-last item with a link)
        if (breadcrumbs && breadcrumbs.length > 1) {
          const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
          if (previousBreadcrumb?.link) {
            return {
              text: previousBreadcrumb.label,
              url: previousBreadcrumb.link,
            };
          }
        }

        // Fallback to calendar view page if no previous page in breadcrumbs
        if (calendarViewPage) {
          return {
            text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
            url: calendarViewPage,
          };
        }

        return null;
      }),
    );

    this.currentPageTitle$ = this.breadcrumbs$.pipe(
      map((breadcrumbs) => {
        return breadcrumbs[breadcrumbs.length - 1]?.label || '';
      }),
    );

    this.actions = toSignal(this.navigationService.actions$);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.navigationService.hideBackNavigation$.subscribe((value) => {
        this.hideBackNavigation = value;
      }),
      this.navigationService.displayMyMeetingsActions$.subscribe((value) => {
        this.displayMyMeetingsActions = value;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
